// "Who sees which data" — shared by the user page and the data map. Mirrors
// the server: user_admin.assistants_for (which assistants a user's modules
// use), data_agent/sources (which data set each reads: the user's own, else
// the instance's) and the profiles' scope_from_user (which rows).
//
//   person ──► assistant (Bro EFD / Bro PPI) ──► data set (whose data) ──► database
//
// A data set carries a data model (EFD / PPI) and a variant (how that
// client's data was loaded — src/data_agent/assistants.py).

import { SCOPE_FIELDS } from './ui';
import { routingTargets } from './context';

export const CARD_GROUPS = ['cards'];
// Own audio cards read the data sets of the user's assistants — EFD when the
// user has none (server: user_admin.assistants_for, audio_cards/data.py).
const AUDIO_CARDS = 'audio_cards';
export const NO_DATA_SET = 'none';
const DEFAULT_VARIANT = { efd: 'mh', ppi: 'ppi' };

export const assistantLabel = (meta, key) => meta.assistants?.find((a) => a.key === key)?.label || key;
export const variantLabel = (meta, key) => meta.variants?.find((v) => v.key === key)?.label || key || '';
export const modelLabel = (key) => (key || '').toUpperCase();

/** The assistants whose data set a user's modules read ('efd', 'ppi'). */
export function assistantsFor(ui, meta) {
  const byKey = Object.fromEntries((meta.modules || []).map((m) => [m.key, m]));
  const out = [];
  (ui.expandables || []).forEach((key) => {
    const a = byKey[key]?.assistant;
    if (a && !out.includes(a)) out.push(a);
  });
  if (!out.length && (ui.expandables || []).includes(AUDIO_CARDS)) out.push('efd');
  return out;
}

/** The data set an assistant reads for a user.
 *  sets: data sets of the instance [{key, name, model, variant, ...}]
 *  defaults: {assistant: data set key} — the instance's data set per assistant
 *  -> {set, origin: 'user'|'default'|'none'|'env', variant} (variant null = no access) */
export function dataSetOf(assistant, ui, sets, defaults) {
  const own = (ui.data_sources || {})[assistant];
  if (own === NO_DATA_SET) return { set: null, origin: 'none', variant: null };
  const ownSet = own && sets.find((s) => s.key === own);
  if (ownSet) return { set: ownSet, origin: 'user', variant: ownSet.variant || null };
  const def = defaults?.[assistant] && sets.find((s) => s.key === defaults[assistant]);
  if (def) return { set: def, origin: 'default', variant: def.variant || null };
  return { set: null, origin: 'env', variant: DEFAULT_VARIANT[assistant] || null };
}

/** The scope fields that limit what a user sees through one assistant. */
export function scopeFieldsFor(assistant, variant, ui, modules) {
  // EFD survista: the whole book comes with the module (efd_survista_agent/profile.py).
  if (!variant || variant === 'efd_survista') return [];
  const enabled = ui.expandables || [];
  const used = new Set();
  modules.forEach((m) => {
    if (enabled.includes(m.key) && m.assistant === assistant) (m.scope || []).forEach((f) => used.add(f));
  });
  return Object.keys(SCOPE_FIELDS).filter((f) => used.has(f));
}

/** What the user sees through one assistant: { label, value, limited }. */
export function scopeOf(assistant, variant, ui, fields) {
  if (assistant === 'ppi') {
    if (ui.ppi_portfolio) return { label: 'Portfolio', value: ui.ppi_portfolio, limited: true };
    if (ui.ppi_consolidation) return { label: 'Consolidation', value: ui.ppi_consolidation, limited: true };
    return { value: 'Whole tenant', limited: false };
  }
  if (fields.includes('rm_name') && ui.rm_name) return { label: 'Advisor', value: ui.rm_name, limited: true };
  return { value: variant === 'efd_survista' ? 'Whole book' : 'Whole bank', limited: false };
}

/** The part a restricted user sees, worded: "advisor Bodmer, Sinan", or just
 *  "Advisor 06" when the value already says what it is (anonymised data). */
export const scopeWhat = (scope) => (scope.label && !scope.value.toLowerCase().startsWith(scope.label.toLowerCase())
  ? `${scope.label.toLowerCase()} ${scope.value}` : scope.value);

/** The modules through which a user reads one assistant's data set
 *  ("Assistant Bro EFD", "Own audio cards"). */
export function usedBy(assistant, ui, modules) {
  const enabled = ui.expandables || [];
  const mods = modules.filter((m) => enabled.includes(m.key) && m.assistant === assistant);
  const audio = modules.find((m) => m.key === AUDIO_CARDS);
  if (audio && enabled.includes(AUDIO_CARDS) && (mods.length || assistant === 'efd')) mods.push(audio);
  return mods.map((m) => m.label);
}

/** The physical database a data set lives in — several data sets can share
 *  one (Marcuard Heritage and its anonymised copy both sit in ip_banking). */
export function databaseOf(set, envTargets, assistant) {
  if (set) {
    return {
      id: `db:${set.db_host}:${set.db_port}/${set.db_name}`, model: set.model,
      detail: `${set.db_name} on ${set.db_host}:${set.db_port}`,
    };
  }
  const env = envTargets?.[assistant];
  return env && env.host
    ? { id: `env:${env.host}:${env.port}/${env.db_name}`, model: assistant, env: true,
      detail: `${env.db_name} on ${env.host}:${env.port} (server configuration)` }
    : { id: `unknown:${assistant}`, model: assistant, detail: '' };
}

/**
 * The whole picture for one instance.
 *   sets       data sets [{key, name, model, variant, db_host, db_port, db_name, ...}]
 *   defaults   {assistant: data set key}
 *   envTargets {assistant: {host, port, db_name}} — where an assistant without data set reads
 * Returns { paths, routed, none }: one path per user and assistant.
 */
export function accessModel(users, meta, sets, defaults, envTargets) {
  const targets = routingTargets(meta);
  const paths = [];
  const routed = [];
  const none = [];
  users.forEach((u) => {
    const ui = u.ui_config || {};
    const inactive = u.status === 'inactive';
    if (ui.home_instance) {
      const t = targets.find((x) => x.key === ui.home_instance) || { key: ui.home_instance, label: ui.home_instance, url: '' };
      routed.push({ user: u, instance: t, inactive });
      return;
    }
    const assistants = assistantsFor(ui, meta);
    if (assistants.length === 0) { none.push({ user: u, inactive }); return; }
    assistants.forEach((a) => {
      const found = dataSetOf(a, ui, sets, defaults);
      const fields = scopeFieldsFor(a, found.variant, ui, meta.modules || []);
      paths.push({
        user: u, assistant: a, set: found.set, origin: found.origin, variant: found.variant,
        noAccess: !found.variant, inactive,
        db: found.variant ? databaseOf(found.set, envTargets, a) : null,
        scope: scopeOf(a, found.variant, ui, fields),
        via: usedBy(a, ui, meta.modules || []),
      });
    });
  });
  return { paths, routed, none };
}
